import React, { useCallback, useEffect, useRef } from 'react';

import type { EChartsOption } from 'echarts';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import { AriaComponent, DataZoomComponent, GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { init, use as registerECharts } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { reportSurfaces } from '~/configs/unifiedAnalyticsTheme';

import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

registerECharts([
  BarChart,
  LineChart,
  PieChart,
  AriaComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer,
]);

type Props = {
  option: EChartsOption;
  title: string;
  source: string;
  range: string;
  onSelect?: (value: { product: string; date?: string }) => void;
  height?: number;
};

/** Native ECharts runtime; only the pure chart-option builder is shared with standalone. */
export default function LPCTrend({ option, title, source, range, onSelect, height = 230 }: Props) {
  const theme = useTheme();
  const surfaces = reportSurfaces(theme);
  const textColor = theme.palette.mode === 'dark' ? theme.palette.text.primary : '#222';
  const element = useRef<HTMLDivElement>(null);
  const instance = useRef<ReturnType<typeof init> | undefined>(undefined);
  const pendingOption = useRef<EChartsOption | undefined>(undefined);
  const syncChart = useCallback(() => {
    const node = element.current;
    const chart = instance.current;
    // A hidden or unmounting map has a singular transform at zero size.
    // Keep its pending option until ResizeObserver reports a visible surface.
    if (!node?.clientWidth || !node.clientHeight || !chart || chart.isDisposed()) {
      return;
    }
    chart.resize();
    if (pendingOption.current) {
      chart.setOption(pendingOption.current, { notMerge: true });
      pendingOption.current = undefined;
    }
  }, []);
  const select = useRef(onSelect);
  useEffect(() => {
    select.current = onSelect;
  }, [onSelect]);
  useEffect(() => {
    if (!element.current) {
      return undefined;
    }
    const chart = init(element.current, undefined, { renderer: 'canvas' });
    instance.current = chart;
    chart.on('click', (event: { seriesName?: string; name?: string }) => {
      if (event.seriesName) {
        select.current?.({ product: event.seriesName, date: event.name });
      }
    });
    const observer = new ResizeObserver(syncChart);
    observer.observe(element.current);
    return () => {
      observer.disconnect();
      chart.dispose();
      instance.current = undefined;
    };
  }, [syncChart]);
  useEffect(() => {
    pendingOption.current = {
      grid: { left: 14, right: 22, top: 28, bottom: 68, containLabel: true },
      textStyle: { fontFamily: 'Arial, sans-serif', fontSize: 11, color: textColor },
      ...option,
    };
    syncChart();
  }, [option, syncChart, textColor]);
  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        minWidth: 0,
        bgcolor: surfaces.paper,
        borderColor: surfaces.rule,
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        component="h2"
        variant="h6"
        sx={{
          p: '5px 6px',
          minHeight: 25,
          fontSize: 12,
          fontWeight: 600,
          textAlign: 'center',
          lineHeight: 1.2,
          bgcolor: '#0f464f',
          color: '#fff',
        }}
      >
        {title}
      </Typography>
      <Box
        ref={element}
        role="img"
        aria-label={`${title}. ${range}. ${onSelect ? 'Select a mark to highlight matching values.' : ''}`}
        sx={{
          width: '100%',
          height: 'auto',
          flex: `1 1 ${height >= 350 ? 340 : 300}px`,
          minHeight: height >= 350 ? 340 : 300,
          minWidth: 0,
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'none' }}>
        {source} · {range} · Source reporting dates · Missing values remain gaps.
      </Typography>
    </Paper>
  );
}
